// ══════════════════════════════════════════════════════════
//  #PROGRAM_DASH_V1 (2026-09-24) — Bill's Program panel (Owner Dash, Bill-only)
//  Bill: "piping into DOPE as a Program dash for me to see activity in one place" —
//  Supabase (space, activity), Vercel (deploys), GitHub (repo / security) in one card.
//  Reads ONE endpoint: /api/program-status (server side holds every token; see that file).
//  Collapsed by default so it costs nothing until opened; refreshes every 60s while open.
// ══════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from 'react';

const GB = 1024 * 1024 * 1024;
const DB_QUOTA = 8 * GB;        // Supabase Pro included database
const FILE_QUOTA = 100 * GB;    // Supabase Pro included file storage

const fmtBytes = (b) => {
  const n = Number(b) || 0;
  if (n >= GB) return (n / GB).toFixed(2) + ' GB';
  if (n >= 1048576) return Math.round(n / 1048576) + ' MB';
  if (n >= 1024) return Math.round(n / 1024) + ' KB';
  return n + ' B';
};
const ago = (t) => {
  const ms = Date.now() - new Date(t).getTime();
  if (!isFinite(ms)) return '—';
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + 'm ago';
  const h = Math.round(m / 60);
  if (h < 48) return h + 'h ago';
  return Math.round(h / 24) + 'd ago';
};

export default function ProgramDash({ D, getToken }) {
  const [open, setOpen] = useState(() => { try { return localStorage.getItem('dope_program_open') === '1'; } catch { return false; } });
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try {
      const tok = getToken ? await getToken() : null;
      const r = await fetch('/api/program-status', { headers: { Authorization: `Bearer ${tok || ''}` } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setData(d);
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }, [getToken]);

  useEffect(() => {
    try { localStorage.setItem('dope_program_open', open ? '1' : '0'); } catch {}
    if (!open) return;
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => clearInterval(iv);
  }, [open, load]);

  const sb = data && data.supabase, vc = data && data.vercel, gh = data && data.github, cap = data && data.capacity;
  const light = (sec) => !data ? D.muted : !sec ? D.muted : sec.ok ? D.green : sec.configured === false ? D.gold : D.red;

  const card = { background: D.card, border: `1px solid ${D.line}`, borderRadius: 12, padding: 14, minWidth: 0 };
  const h = { fontSize: 11, fontWeight: 900, letterSpacing: 1.2, textTransform: 'uppercase', color: D.muted, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 };
  const dot = (c) => <span style={{ width: 9, height: 9, borderRadius: '50%', background: c, boxShadow: `0 0 8px ${c}`, display: 'inline-block', flexShrink: 0 }} />;
  const row = { display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5, padding: '4px 0', borderBottom: '1px solid #161C28' };
  const bar = (used, total, label, warn = 50, crit = 80) => {
    const p = Math.min(100, (used / total) * 100);
    const c = p >= crit ? D.red : p >= warn ? D.gold : D.green;
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: D.text, marginBottom: 4 }}>
          <span style={{ fontWeight: 700 }}>{label}</span>
          <span style={{ color: D.muted, fontVariantNumeric: 'tabular-nums' }}>{fmtBytes(used)} / {fmtBytes(total)} · <b style={{ color: c }}>{p < 1 ? '<1' : Math.round(p)}%</b></span>
        </div>
        <div style={{ height: 7, background: D.cardHi, borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ width: Math.max(p, 1) + '%', height: '100%', background: c, borderRadius: 99 }} />
        </div>
      </div>
    );
  };
  const link = (href, txt) => <a href={href} target="_blank" rel="noreferrer" style={{ fontSize: 10.5, fontWeight: 700, color: D.cyan, textDecoration: 'none', marginLeft: 'auto', textTransform: 'none', letterSpacing: 0 }}>{txt} ↗</a>;

  const fileBytes = sb && Array.isArray(sb.buckets) ? sb.buckets.reduce((s, b) => s + (Number(b.bytes) || 0), 0) : 0;

  return (
    <div style={{ background: D.card, border: `1px solid ${D.line}`, borderRadius: 14, padding: 14, marginTop: 16 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontSize: 13, fontWeight: 900, letterSpacing: 1.5, color: D.cyan, textTransform: 'uppercase' }}>Program</span>
        <span style={{ fontSize: 11, color: D.muted }}>Supabase · Vercel · GitHub</span>
        <span style={{ display: 'flex', gap: 6, marginLeft: 8 }}>{dot(light(sb))}{dot(light(vc))}{dot(light(gh))}</span>
        {open && data && <span style={{ fontSize: 10.5, color: D.muted, marginLeft: 8 }}>updated {ago(data.at)}</span>}
        {open && <button onClick={(e) => { e.stopPropagation(); load(); }} disabled={busy}
          style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, color: D.text, background: D.cardHi, border: `1px solid ${D.line}`, borderRadius: 8, padding: '4px 10px', cursor: busy ? 'default' : 'pointer' }}>
          {busy ? 'Loading…' : 'Refresh'}</button>}
        <span style={{ marginLeft: open ? 8 : 'auto', color: D.muted, fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && err && <div style={{ marginTop: 12, fontSize: 12, color: D.red }}>Couldn't load: {err}</div>}

      {open && data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12, marginTop: 12 }}>

          {/* ── CAPACITY ── #PROGRAM_CAPACITY_V1 (2026-09-24) — Bill: "something we need to monitor and scale
              ahead of time as we build out data base and flip the year." Live from the Supabase Metrics API.
              Memory is the real ceiling (DB 2% of plan, files grow ~0.3 GB/mo of 100 GB). Scale rule printed
              on the card so the call doesn't depend on remembering it. */}
          <div style={card}>
            <div style={h}>{dot(!cap ? D.muted : !cap.ok ? D.red : (cap.memUsed / cap.memTotal) >= 0.9 ? D.red : (cap.memUsed / cap.memTotal) >= 0.75 ? D.gold : D.green)} Capacity {link('https://supabase.com/dashboard/project/aufabqhyubdimqpxknsy', 'Open')}</div>
            {!cap || !cap.ok ? <div style={{ fontSize: 12, color: D.red, lineHeight: 1.5 }}>{(cap && cap.error) || 'No data'}</div> : (<>
              {cap.memTotal ? bar(cap.memUsed, cap.memTotal, 'Memory (Micro)', 75, 90) : null}
              {cap.diskTotal ? bar(cap.diskUsed, cap.diskTotal, 'Disk (auto-grows)', 80, 90) : null}
              {cap.load5 != null && <div style={row}><span style={{ color: D.muted }}>CPU load (5-min avg)</span>
                <span style={{ color: cap.cores && cap.load5 > cap.cores ? D.gold : D.text, fontWeight: 700 }}>{cap.load5.toFixed(2)}{cap.cores ? ` of ${cap.cores} cores` : ''}</span></div>}
              {sb && sb.ok && sb.files_30d_bytes != null && <div style={row}><span style={{ color: D.muted }}>New files (30 days)</span>
                <span style={{ color: D.text, fontWeight: 700 }}>{fmtBytes(sb.files_30d_bytes)} <span style={{ color: D.muted, fontWeight: 400 }}>· {sb.files_30d_count} uploads · ~{fmtBytes(sb.files_30d_bytes * 12)}/yr</span></span></div>}
              <div style={{ fontSize: 10.5, color: D.muted, lineHeight: 1.5, marginTop: 10, padding: '8px 10px', background: D.cardHi, borderRadius: 8 }}>
                <b style={{ color: D.text }}>Scale-up rule:</b> memory gold (75%+) most days for a week → go <b style={{ color: D.text }}>Small</b> (2 GB, ~$5/mo more), after hours.
                Red (90%+) → do it that night. Disk grows itself; file storage is years away from its limit.
              </div>
            </>)}
          </div>

          {/* ── SUPABASE ── */}
          <div style={card}>
            <div style={h}>{dot(light(sb))} Supabase {link('https://supabase.com/dashboard/project/aufabqhyubdimqpxknsy', 'Open')}</div>
            {!sb || !sb.ok ? <div style={{ fontSize: 12, color: D.red }}>{(sb && sb.error) || 'No data'}</div> : (<>
              {bar(sb.db_bytes, DB_QUOTA, 'Database')}
              {bar(fileBytes, FILE_QUOTA, 'File storage')}
              <div style={row}><span style={{ color: D.muted }}>Live data in app_storage</span><span style={{ color: D.text, fontWeight: 700 }}>{fmtBytes(sb.app_storage_live_bytes)} <span style={{ color: D.muted, fontWeight: 400 }}>({fmtBytes(sb.app_storage_bytes)} on disk)</span></span></div>
              <div style={row}><span style={{ color: D.muted }}>Connections</span><span style={{ color: sb.connections / sb.max_connections > 0.7 ? D.gold : D.text, fontWeight: 700 }}>{sb.connections} / {sb.max_connections}</span></div>
              <div style={row}><span style={{ color: D.muted }}>Sign-in accounts</span><span style={{ color: D.text, fontWeight: 700 }}>{sb.auth_users}</span></div>
              <div style={row}><span style={{ color: D.muted }}>Data keys touched (24h)</span><span style={{ color: D.text, fontWeight: 700 }}>{sb.writes_24h} of {sb.app_storage_rows}</span></div>
              <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 1, color: D.muted, textTransform: 'uppercase', margin: '12px 0 4px' }}>Buckets</div>
              {(sb.buckets || []).map(b => (
                <div key={b.bucket} style={row}><span style={{ color: D.text }}>{b.bucket}</span><span style={{ color: D.muted, fontVariantNumeric: 'tabular-nums' }}>{b.files} files · <b style={{ color: D.text }}>{fmtBytes(b.bytes)}</b></span></div>
              ))}
            </>)}
          </div>

          {/* ── ACTIVITY (recent saves) ── */}
          <div style={{ ...card, display: 'flex', flexDirection: 'column' }}>
            <div style={h}>{dot(light(sb))} Recent saves</div>
            {!sb || !sb.ok ? <div style={{ fontSize: 12, color: D.muted }}>—</div> : (
              <div style={{ flex: 1, maxHeight: 330, overflowY: 'auto', paddingRight: 6 }}>
                {(sb.recent_keys || []).map(k => (
                  <div key={k.key} style={row}>
                    <span style={{ color: D.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{k.key}</span>
                    <span style={{ color: D.muted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtBytes(k.bytes)} · <b style={{ color: D.text }}>{ago(k.at)}</b></span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── VERCEL ── */}
          <div style={{ ...card, display: 'flex', flexDirection: 'column' }}>
            <div style={h}>{dot(light(vc))} Vercel deploys {link('https://vercel.com/wearedakota/dakota-ops/deployments', 'Open')}</div>
            {!vc || !vc.ok ? (
              <div style={{ fontSize: 12, color: vc && vc.configured === false ? D.gold : D.red, lineHeight: 1.5 }}>
                {vc && vc.configured === false
                  ? <>Not connected yet. Add <b>VERCEL_API_TOKEN</b> in Vercel → dakota-ops → Settings → Environment Variables, then redeploy.</>
                  : (vc && vc.error) || 'No data'}
              </div>
            ) : (<>
              {(() => {   // #VERCEL_DROPS_V2 — real counts (today from local midnight, 24h, 7-day avg), not capped at 12
                const t = Array.isArray(vc.times) ? vc.times : [];
                const mid = new Date(); mid.setHours(0, 0, 0, 0);
                const today = t.filter(x => x[0] >= mid.getTime()).length;
                const failToday = t.filter(x => x[0] >= mid.getTime() && x[1]).length;
                const avg = t.length ? (t.length / 7) : 0;
                const big = (v, l, c) => <div><div style={{ fontSize: 22, fontWeight: 900, color: c }}>{v}</div><div style={{ fontSize: 10, color: D.muted }}>{l}</div></div>;
                return (
                  <div style={{ display: 'flex', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
                    {big(t.length ? today : vc.last24h, t.length ? 'today' : 'last 24h', D.cyan)}
                    {t.length ? big(vc.last24h, 'last 24h', D.text) : null}
                    {t.length ? big(avg.toFixed(1), '/day · 7-day avg', D.text) : null}
                    {big(t.length ? failToday : vc.failed, t.length ? 'failed today' : 'failed (last 12)', (t.length ? failToday : vc.failed) ? D.red : D.green)}
                  </div>
                );
              })()}
              <div style={{ flex: 1, maxHeight: 290, overflowY: 'auto', paddingRight: 6 }}>
                {vc.deployments.map((x, i) => (
                  <a key={x.id} href={x.inspector || undefined} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none', padding: '5px 0', borderBottom: '1px solid #161C28' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: D.muted }}>
                      {dot(x.state === 'READY' ? D.green : x.state === 'ERROR' ? D.red : D.gold)}
                      <span style={{ fontWeight: 800, color: x.state === 'ERROR' ? D.red : D.text }}>{x.state}</span>
                      {i === 0 && x.target === 'production' && x.state === 'READY' && <span style={{ color: D.cyan, fontWeight: 800 }}>LIVE</span>}
                      <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{x.sha} · {ago(x.created)}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: D.text, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.msg || '(no message)'}</div>
                  </a>
                ))}
              </div>
            </>)}
          </div>

          {/* ── GITHUB ── */}
          <div style={card}>
            <div style={h}>{dot(light(gh))} GitHub {link('https://github.com/DAKOTAOPS/dakota-ops', 'Open')}</div>
            {!gh || !gh.ok ? (
              <div style={{ fontSize: 12, color: gh && gh.configured === false ? D.gold : D.red, lineHeight: 1.5 }}>
                {gh && gh.configured === false
                  ? <>Optional. Add <b>GITHUB_TOKEN</b> (read-only, this repo) in Vercel env vars to show repo + security alerts here.</>
                  : (gh && gh.error) || 'No data'}
              </div>
            ) : (<>
              <div style={row}><span style={{ color: D.muted }}>Visibility</span><span style={{ color: gh.private ? D.green : D.red, fontWeight: 800 }}>{gh.private ? 'Private' : 'PUBLIC'}</span></div>
              <div style={row}><span style={{ color: D.muted }}>Last push</span><span style={{ color: D.text, fontWeight: 700 }}>{ago(gh.pushedAt)}</span></div>
              <div style={row}><span style={{ color: D.muted }}>Repo size</span><span style={{ color: D.text, fontWeight: 700 }}>{fmtBytes((gh.sizeKb || 0) * 1024)}</span></div>
              <div style={row}><span style={{ color: D.muted }}>Open issues</span><span style={{ color: D.text, fontWeight: 700 }}>{gh.openIssues}</span></div>
              <div style={row}><span style={{ color: D.muted }}>Dependabot alerts</span>
                <span style={{ color: gh.alerts ? D.gold : gh.alerts === 0 ? D.green : D.muted, fontWeight: 800 }}>{gh.alerts != null ? gh.alerts : (gh.alertsNote || '—')}</span></div>
            </>)}
          </div>
        </div>
      )}
    </div>
  );
}
